import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@/features/auth/AuthProvider";
import { getRevenueCatApiKey } from "@/lib/env";
import {
  getPurchases,
  isUserCancelledError,
  type NormalizedPackage,
} from "@/lib/purchases";

export type RevenueCatState =
  | { status: "loading" }
  /** `no-key`: no public SDK key for this platform; `signed-out`: no Supabase session. */
  | { status: "unconfigured"; reason: "no-key" | "signed-out" }
  | {
      status: "error";
      /** Which step failed — only purchase/restore errors are reset on reopen. */
      source: "configure" | "purchase" | "restore";
      message: string;
    }
  | { status: "ready" }
  | { status: "purchasing" }
  | { status: "purchased" }
  | { status: "restoring" }
  | { status: "restored"; hasEntitlement: boolean };

/** Outcome of {@link UseRevenueCatReturn.restore}, for callers that toast it. */
export type RestoreOutcome =
  | { status: "restored"; hasEntitlement: boolean }
  | { status: "error" };

export interface UseRevenueCatReturn {
  state: RevenueCatState;
  packages: NormalizedPackage[];
  purchase: (pkg: NormalizedPackage) => Promise<void>;
  /** Re-sync store receipts (App Store guideline 3.1.1). */
  restore: () => Promise<RestoreOutcome>;
  /** Clear a finished purchase/restore outcome (e.g. when the paywall reopens). */
  resetPurchaseState: () => void;
  isNative: boolean;
}

// ── Module-level SDK session ─────────────────────────────────────────────────
//
// The SDK is a process-wide singleton, but several components mount
// useRevenueCat (the global PaywallSheet, CookingChatSheet's own sheet…).
// Configuration is therefore shared: the first hook instance for a user
// configures the SDK, the others await the same promise instead of calling
// Purchases.configure() again.

let sessionUserId: string | null = null;
let sessionPackages: Promise<NormalizedPackage[]> | null = null;

// Identity operations (configure / logIn / logOut) on the SDK singleton run
// one at a time, in call order. Otherwise a sign-out's logOut() still in
// flight could land *after* the next user's logIn() and leave the SDK
// anonymous, and two quick switches could interleave configure/logIn.
let identityQueue: Promise<unknown> = Promise.resolve();

function enqueueIdentityOp<T>(op: () => Promise<T>): Promise<T> {
  const run = identityQueue.then(op, op);
  identityQueue = run.catch(() => {});
  return run;
}

function ensureConfigured(
  apiKey: string,
  userId: string,
): Promise<NormalizedPackage[]> {
  if (sessionUserId === userId && sessionPackages) return sessionPackages;
  const purchases = getPurchases();
  sessionUserId = userId;
  const pending = (async () => {
    await enqueueIdentityOp(() => purchases.configure(apiKey, userId));
    return purchases.getAllPackages();
  })();
  sessionPackages = pending;
  // A failed configuration must be retryable by the next mount.
  pending.catch(() => {
    if (sessionPackages === pending) {
      sessionPackages = null;
      sessionUserId = null;
    }
  });
  return pending;
}

function endSession(): void {
  if (sessionUserId === null) return;
  sessionUserId = null;
  sessionPackages = null;
  void enqueueIdentityOp(() => getPurchases().logOut()).catch(() => {});
}

/** Test helper: forget the shared SDK session. */
export function __resetRevenueCatSession(): void {
  sessionUserId = null;
  sessionPackages = null;
  identityQueue = Promise.resolve();
}

function describeError(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback;
  let message = err.message;
  // PurchasesError from purchases-js carries errorCode + underlyingErrorMessage.
  const e = err as unknown as Record<string, unknown>;
  if (e.errorCode != null) message += ` [${String(e.errorCode)}]`;
  if (e.underlyingErrorMessage)
    message += ` — ${String(e.underlyingErrorMessage)}`;
  return message;
}

/**
 * Hook to integrate RevenueCat purchases via the platform SDK.
 *
 * On native (iOS/Android) uses @revenuecat/purchases-capacitor.
 * On web uses @revenuecat/purchases-js (Web Billing).
 * The public SDK key is chosen per platform (Capacitor.getPlatform()).
 *
 * IMPORTANT: the SDK is configured with appUserID = Supabase user id so that
 * backend webhook events carry the correct app_user_id and verify_entitlement
 * can match them to the JWT sub. Without this, the backend gate is invisible.
 *
 * The backend (RevenueCat REST API + webhook) remains the source of truth for
 * entitlements. This hook only drives the client-side purchase/trial flow.
 */
export function useRevenueCat(): UseRevenueCatReturn {
  const [state, setState] = useState<RevenueCatState>({ status: "loading" });
  const [packages, setPackages] = useState<NormalizedPackage[]>([]);
  const { session } = useAuth();
  const userId = session?.user.id ?? null;
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    const apiKey = getRevenueCatApiKey(Capacitor.getPlatform());
    if (!apiKey) {
      setState({ status: "unconfigured", reason: "no-key" });
      return;
    }

    if (!userId) {
      endSession();
      setPackages([]);
      setState({ status: "unconfigured", reason: "signed-out" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });
    ensureConfigured(apiKey, userId).then(
      (pkgs) => {
        if (cancelled) return;
        setPackages(pkgs);
        setState({ status: "ready" });
      },
      (err: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          source: "configure",
          message: describeError(err, "Failed to configure RevenueCat"),
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const purchase = useCallback(async (pkg: NormalizedPackage) => {
    setState({ status: "purchasing" });
    try {
      await getPurchases().purchasePackage(pkg);
      setState({ status: "purchased" });
    } catch (err) {
      if (isUserCancelledError(err)) {
        // Closing the store sheet is not an error: back to the plan picker.
        setState((prev) =>
          prev.status === "purchasing" ? { status: "ready" } : prev,
        );
        return;
      }
      console.error("[RC] purchasePackage failed:", err);
      const message = describeError(err, "Achat échoué");
      setState((prev) =>
        prev.status === "purchasing"
          ? { status: "error", source: "purchase", message }
          : prev,
      );
    }
  }, []);

  const restore = useCallback(async (): Promise<RestoreOutcome> => {
    setState({ status: "restoring" });
    try {
      const hasEntitlement = await getPurchases().restorePurchases();
      setState({ status: "restored", hasEntitlement });
      return { status: "restored", hasEntitlement };
    } catch (err) {
      console.error("[RC] restorePurchases failed:", err);
      setState({
        status: "error",
        source: "restore",
        message: describeError(err, "Restauration échouée"),
      });
      return { status: "error" };
    }
  }, []);

  const resetPurchaseState = useCallback(() => {
    setState((prev) => {
      if (
        prev.status === "purchased" ||
        prev.status === "restored" ||
        (prev.status === "error" && prev.source !== "configure")
      ) {
        return { status: "ready" };
      }
      return prev;
    });
  }, []);

  return { state, packages, purchase, restore, resetPurchaseState, isNative };
}
